import React, { useEffect, useState, createContext, useContext } from 'react';
import { poiCategories } from '../data/mockData';
// Define the travel party types - now only 3 personas
export type TravelPartyType = 'couple' | 'youngster' | 'family';
// Define which POI types are most relevant for each travel party type
export const poiRelevanceByPartyType = {
  couple: ['restaurant', 'bar', 'park', 'museum', 'attraction'],
  youngster: ['bar', 'coffeeshop', 'restaurant', 'attraction', 'shopping'],
  family: ['park', 'attraction', 'restaurant', 'supermarket', 'museum']
};
// Define preset cities with their recommended POIs
export const cityPresets = {
  amsterdam: {
    name: 'Amsterdam',
    defaultPOIs: ['attraction', 'museum', 'restaurant', 'transport'],
    image: 'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  },
  rotterdam: {
    name: 'Rotterdam',
    defaultPOIs: ['attraction', 'shopping', 'restaurant', 'transport'],
    image: 'https://images.unsplash.com/photo-1512569974449-82c2cb45e6f0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
  },
  utrecht: {
    name: 'Utrecht',
    defaultPOIs: ['park', 'restaurant', 'museum', 'bar'],
    image: 'https://images.unsplash.com/photo-1558551649-e44c8f992010?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  },
  'the hague': {
    name: 'The Hague',
    defaultPOIs: ['attraction', 'park', 'restaurant', 'shopping'],
    image: 'https://images.unsplash.com/photo-1577395119662-d7d0ebb79064?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80'
  },
  groningen: {
    name: 'Groningen',
    defaultPOIs: ['bar', 'restaurant', 'museum', 'park'],
    image: 'https://images.unsplash.com/photo-1558288093-3c990f33d5a4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  },
  maastricht: {
    name: 'Maastricht',
    defaultPOIs: ['museum', 'restaurant', 'shopping', 'attraction'],
    image: 'https://images.unsplash.com/photo-1600623471616-8c1966c91ff6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }
};
// Get all available POI types
export const allPOITypes = poiCategories.map(category => category.type);
interface UserProfileContextType {
  travelParty: TravelPartyType;
  setTravelParty: (party: TravelPartyType) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  recentSearches: string[];
  addRecentSearch: (search: string) => void;
  selectedPOIs: string[];
  setSelectedPOIs: (pois: string[]) => void;
  selectedCity: string | null;
  setSelectedCity: (city: string | null) => void;
  applyPresetPOIs: (city: string) => void;
}
const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);
export const UserProfileProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // Load saved preferences from localStorage if available
  const [travelParty, setTravelParty] = useState<TravelPartyType>(() => {
    const saved = localStorage.getItem('travelParty');
    // Migrate old values to new personas
    if (saved === 'solo' || saved === 'friends') {
      return 'youngster';
    }
    return saved as TravelPartyType || 'couple';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    const saved = localStorage.getItem('recentSearches');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedPOIs, setSelectedPOIs] = useState<string[]>(() => {
    const saved = localStorage.getItem('selectedPOIs');
    return saved ? JSON.parse(saved) : [];
  });
  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('travelParty', travelParty);
  }, [travelParty]);
  useEffect(() => {
    localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
  }, [recentSearches]);
  useEffect(() => {
    localStorage.setItem('selectedPOIs', JSON.stringify(selectedPOIs));
  }, [selectedPOIs]);
  const addRecentSearch = (search: string) => {
    if (!search.trim()) return;
    setRecentSearches(prev => {
      // Add to the beginning and remove duplicates
      const newSearches = [search, ...prev.filter(s => s !== search)].slice(0, 5);
      return newSearches;
    });
  };
  // Apply preset POIs for a selected city
  const applyPresetPOIs = (city: string) => {
    const cityKey = city.toLowerCase();
    if (cityPresets[cityKey]) {
      // Get default POIs for the city
      let defaultPOIs = cityPresets[cityKey].defaultPOIs;
      // Add travel party specific POIs
      const relevantPOIs = poiRelevanceByPartyType[travelParty];
      // Combine default city POIs with travel party POIs (without duplicates)
      const combinedPOIs = [...new Set([...defaultPOIs, ...relevantPOIs.slice(0, 2)])];
      setSelectedPOIs(combinedPOIs);
      setSelectedCity(cityKey);
    }
  };
  return <UserProfileContext.Provider value={{
    travelParty,
    setTravelParty,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    selectedPOIs,
    setSelectedPOIs,
    selectedCity,
    setSelectedCity,
    applyPresetPOIs
  }} data-id="element-234">
      {children}
    </UserProfileContext.Provider>;
};
export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};